import React from 'react';
import NavigationHeader from '../components/NavigationHeader';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { MapPin, Home, Utensils, Dumbbell, Wifi, Shield, GraduationCap } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';

// User-provided Oakham facility images
const oakhamImages = {
  kitchen: "https://customer-assets.emergentagent.com/job_e254ab9b-cad2-45bc-a536-33ed86a8849e/artifacts/ticeky3u_Copia%20de%2013cab3ae-6b93-47f4-88c7-647f6bb28de7.jpeg",
  diningHall: "https://customer-assets.emergentagent.com/job_e254ab9b-cad2-45bc-a536-33ed86a8849e/artifacts/6i35qsj3_Copia%20de%20WhatsApp%20Image%202025-02-10%20at%2012.42.44%20%281%29.jpeg",
  recreationRoom: "https://customer-assets.emergentagent.com/job_e254ab9b-cad2-45bc-a536-33ed86a8849e/artifacts/2sjb73a8_Copia%20de%20WhatsApp%20Image%202025-02-10%20at%2012.42.43%20%283%29.jpeg",
  lectureHall: "https://customer-assets.emergentagent.com/job_e254ab9b-cad2-45bc-a536-33ed86a8849e/artifacts/gzotpcmb_Copia%20de%20WhatsApp%20Image%202025-02-10%20at%2012.42.44.jpeg",
  foodService: "https://customer-assets.emergentagent.com/job_e254ab9b-cad2-45bc-a536-33ed86a8849e/artifacts/to9sl1ju_Academy_Camp_2025-93.jpg"
};

const AboutOakhamPage = () => {
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationHeader />
      
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center overflow-hidden mt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${oakhamImages.diningHall})` }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
          <h1 className="text-5xl md:text-6xl font-black mb-4">
            {t('aboutOakham.heroTitle')}
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 font-medium">
            {t('aboutOakham.heroDesc')}
          </p>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center mb-8">
            <MapPin className="h-10 w-10 text-green-600 mr-4" />
            <h2 className="text-4xl font-black text-gray-900">{t('aboutOakham.perfectLocation')}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="p-8 text-center">
              <h3 className="text-2xl font-black text-gray-900 mb-4">{t('aboutOakham.locationTown')}</h3>
              <p className="text-gray-600 mb-4">{t('aboutOakham.locationRegion')}</p>
              <p className="text-green-600 font-bold">{t('aboutOakham.locationFromLondon')}</p>
            </Card>
            <Card className="p-8">
              <h3 className="text-xl font-black text-gray-900 mb-4">{t('aboutOakham.nearestAirports')}</h3>
              <ul className="space-y-2">
                {(t('aboutOakham.airports') || []).map((airport, idx) => (
                  <li key={idx} className="text-gray-700 flex items-center">
                    <span className="text-green-600 mr-2">✓</span>
                    {airport}
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Accommodation Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center mb-12">
            <Home className="h-10 w-10 text-green-600 mr-4" />
            <h2 className="text-4xl font-black text-gray-900">{t('aboutOakham.accommodation')}</h2>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            <div className="space-y-6">
              <Card className="p-8">
                <h3 className="text-2xl font-black text-gray-900 mb-4">{t('aboutOakham.rooms')}</h3>
                <p className="text-gray-700 mb-4">{t('aboutOakham.roomTypes')}</p>
                <p className="text-gray-600 text-sm">{t('aboutOakham.bathrooms')} {t('aboutOakham.bathroomsDesc')}</p>
              </Card>
              
              <Card className="p-8">
                <h3 className="text-2xl font-black text-gray-900 mb-4">{t('aboutOakham.amenities')}</h3>
                <ul className="space-y-2">
                  {(t('aboutOakham.amenitiesList') || []).map((amenity, idx) => (
                    <li key={idx} className="text-gray-700 flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      <span className="text-sm">{amenity}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <img src={oakhamImages.kitchen} alt="Kitchen Facilities" className="rounded-lg h-48 w-full object-cover" />
              <img src={oakhamImages.recreationRoom} alt="Recreation Room" className="rounded-lg h-48 w-full object-cover" />
              <img src={oakhamImages.lectureHall} alt="Lecture Hall" className="rounded-lg h-48 w-full object-cover col-span-2" />
            </div>
          </div>
        </div>
      </section>

      {/* Dining Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center mb-12">
            <Utensils className="h-10 w-10 text-green-600 mr-4" />
            <h2 className="text-4xl font-black text-gray-900">{t('aboutOakham.dining')}</h2>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <img src={oakhamImages.diningHall} alt="Dining Hall" className="rounded-lg w-full h-64 object-cover mb-4" />
              <img src={oakhamImages.foodService} alt="Food Service" className="rounded-lg w-full h-64 object-cover" />
            </div>
            
            <div className="space-y-6">
              <Card className="p-8">
                <h3 className="text-2xl font-black text-gray-900 mb-4">{t('aboutOakham.mealsNutrition')}</h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>{t('aboutOakham.meals')}</strong> {t('aboutOakham.mealsDesc')}</p>
                  <p><strong>{t('aboutOakham.cuisine')}</strong> {t('aboutOakham.cuisineDesc')}</p>
                  <p><strong>{t('aboutOakham.dietary')}</strong> {t('aboutOakham.dietaryDesc')}</p>
                </div>
              </Card>
              
              <Card className="p-8">
                <h3 className="text-xl font-black text-gray-900 mb-4">{t('aboutOakham.features')}</h3>
                <ul className="grid grid-cols-2 gap-2">
                  {(t('aboutOakham.diningFeatures') || []).map((feature, idx) => (
                    <li key={idx} className="text-gray-700 flex items-start text-sm">
                      <span className="text-green-600 mr-2">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Facilities Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-black text-gray-900 text-center mb-12">{t('aboutOakham.worldClassFacilities')}</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Sports Facilities */}
            <Card className="p-6">
              <Dumbbell className="h-12 w-12 text-green-600 mb-4" />
              <h3 className="text-xl font-black text-gray-900 mb-4">{t('aboutOakham.sportsFacilities')}</h3>
              <ul className="space-y-2">
                {(t('aboutOakham.sportsFeatures') || []).map((facility, idx) => (
                  <li key={idx} className="text-gray-700 text-sm flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    {facility}
                  </li>
                ))}
              </ul>
            </Card>
            
            {/* Academic Facilities */}
            <Card className="p-6">
              <GraduationCap className="h-12 w-12 text-blue-600 mb-4" />
              <h3 className="text-xl font-black text-gray-900 mb-4">{t('aboutOakham.academicFacilities')}</h3>
              <ul className="space-y-2">
                {(t('aboutOakham.academicFeatures') || []).map((facility, idx) => (
                  <li key={idx} className="text-gray-700 text-sm flex items-start">
                    <span className="text-blue-600 mr-2">✓</span>
                    {facility}
                  </li>
                ))}
              </ul>
            </Card>
            
            {/* Recreational Facilities */}
            <Card className="p-6">
              <Wifi className="h-12 w-12 text-purple-600 mb-4" />
              <h3 className="text-xl font-black text-gray-900 mb-4">{t('aboutOakham.recreational')}</h3>
              <ul className="space-y-2">
                {(t('aboutOakham.recreationalFeatures') || []).map((facility, idx) => (
                  <li key={idx} className="text-gray-700 text-sm flex items-start">
                    <span className="text-purple-600 mr-2">✓</span>
                    {facility}
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Safety Section */}
      <section className="py-16 bg-gradient-to-br from-green-600 to-green-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="h-16 w-16 mx-auto mb-6" />
          <h2 className="text-4xl font-black mb-6">{t('aboutOakham.safetySecurity')}</h2>
          <p className="text-xl text-green-100 mb-8">
            {t('aboutOakham.safetyDesc')}
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <p className="font-black text-2xl mb-2">24/7</p>
              <p className="text-sm">{t('aboutOakham.staffSupervision')}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <p className="font-black text-2xl mb-2">{t('aboutOakham.safe')}</p>
              <p className="text-sm">{t('aboutOakham.townEnvironment')}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <p className="font-black text-2xl mb-2">{t('aboutOakham.onSite')}</p>
              <p className="text-sm">{t('aboutOakham.medicalCare')}</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutOakhamPage;
